import { useEffect, useState } from 'react'
import {
  storeTokens,
  parseJwt,
  COGNITO_DOMAIN,
  COGNITO_CLIENT_ID,
  REDIRECT_URI,
  PKCE_VERIFIER_KEY,
  OAUTH_STATE_KEY,
} from '../contexts/AuthContext'

// Session storage key for return URL (must match Login.tsx)
const RETURN_URL_KEY = 'fd_qa_tools_return_url'

// Auth token keys stored in localStorage — mirrors clearAuth() in AuthContext
const AUTH_TOKEN_KEYS = ['accessToken', 'idToken', 'refreshToken', 'expiresAt']
function clearAuthTokens() {
  AUTH_TOKEN_KEYS.forEach((key) => localStorage.removeItem(key))
}

export default function AuthCallback() {
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    handleCallback()
  }, [])

  const handleCallback = async () => {
    try {
      const params = new URLSearchParams(window.location.search)
      const code = params.get('code')
      const errorParam = params.get('error')
      const returnedState = params.get('state')

      if (errorParam) {
        setError(`Authentication error: ${errorParam}`)
        return
      }

      if (!code) {
        setError('No authorization code found')
        return
      }

      // Validate CSRF state — must match what was stored before the redirect
      const storedState = sessionStorage.getItem(OAUTH_STATE_KEY)
      sessionStorage.removeItem(OAUTH_STATE_KEY)
      if (!storedState || storedState !== returnedState) {
        setError('Authentication failed: invalid state parameter')
        return
      }

      // Retrieve PKCE verifier stored before the redirect
      const codeVerifier = sessionStorage.getItem(PKCE_VERIFIER_KEY)
      sessionStorage.removeItem(PKCE_VERIFIER_KEY)
      if (!codeVerifier) {
        setError('Authentication failed: missing PKCE verifier')
        return
      }

      // Exchange authorization code for tokens (includes PKCE verifier)
      const response = await fetch(`${COGNITO_DOMAIN}/oauth2/token`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: new URLSearchParams({
          grant_type: 'authorization_code',
          client_id: COGNITO_CLIENT_ID,
          code: code,
          redirect_uri: REDIRECT_URI,
          code_verifier: codeVerifier,
        }),
      })

      if (!response.ok) {
        const errorData = await response.json()
        throw new Error(errorData.error_description || 'Failed to exchange code for tokens')
      }

      const data = await response.json()

      // Verify email domain BEFORE storing tokens — reject non-corporate accounts
      // without ever persisting their credentials.
      const userInfo = parseJwt(data.id_token)
      if (!userInfo?.email || !userInfo.email.endsWith('@fiveanddone.com')) {
        sessionStorage.removeItem(RETURN_URL_KEY)
        setError('Only @fiveanddone.com email addresses are allowed')
        return
      }

      // Domain verified — safe to persist tokens
      storeTokens(data)

      // Validate return URL to prevent open redirect attacks.
      // Must be a relative path (starts with /) and not a protocol-relative URL (//).
      let returnUrl = sessionStorage.getItem(RETURN_URL_KEY) || '/'
      sessionStorage.removeItem(RETURN_URL_KEY)
      if (!returnUrl.startsWith('/') || returnUrl.startsWith('//')) {
        returnUrl = '/'
      }

      // Full reload so AuthContext picks up the newly stored tokens
      window.location.href = returnUrl
    } catch (err) {
      console.error('Callback error:', err)
      clearAuthTokens()
      setError(err instanceof Error ? err.message : 'Authentication failed')
    }
  }

  if (error) {
    return (
      <div className="min-h-screen bg-linear-bg flex items-center justify-center p-4">
        <div className="bg-linear-bg-card border border-linear-border-input rounded-xl shadow-xl p-8 max-w-md w-full">
          <div className="text-center mb-6">
            <svg
              className="w-16 h-16 text-error mx-auto mb-4"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
              />
            </svg>
            <h2 className="text-2xl font-bold text-linear-text mb-2">Authentication Error</h2>
            <p className="text-linear-text-secondary mb-6">{error}</p>
          </div>
          <button
            onClick={() => (window.location.href = '/')}
            className="btn btn-primary w-full"
          >
            Go to Home
          </button>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-linear-bg flex items-center justify-center">
      <div className="text-center">
        <div className="spinner spinner-lg mx-auto mb-4"></div>
        <div className="text-linear-text-secondary">Completing authentication...</div>
      </div>
    </div>
  )
}
